import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AboutUs = () => {
  // Organization schema for SEO
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Dentist",
    "name": "Fort Worth Dental",
    "description": "Family and cosmetic dentistry serving Fort Worth TX since 1989. Expert dental implants, Invisalign, LANAP laser gum treatment, and comprehensive dental care.",
    "url": "https://fortworthdental.com",
    "logo": "https://fortworthdental.com/logo-fwd.png",
    "image": "https://fortworthdental.com/images/team/team-photo.jpg",
    "telephone": "(817) 985-3337",
    "email": "recall@fortworthdental.com",
    "foundingDate": "1989",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "4620 Citylake Blvd W",
      "addressLocality": "Fort Worth",
      "addressRegion": "TX",
      "postalCode": "76132",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "32.7284",
      "longitude": "-97.4358"
    },
    "areaServed": ["Fort Worth", "Aledo", "Benbrook", "White Settlement", "Weatherford", "Arlington", "Burleson"],
    "priceRange": "$$",
    "openingHoursSpecification": [
      {"@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday"], "opens": "08:00", "closes": "17:00"},
      {"@type": "OpeningHoursSpecification", "dayOfWeek": "Friday", "opens": "08:00", "closes": "14:00"}
    ],
    "sameAs": [
      "https://www.facebook.com/FWDental",
      "https://x.com/FortWorthTeeth",
      "https://www.youtube.com/channel/UCUFwAVnty9mZPxUYT9VLyIA",
      "https://www.yelp.com/biz/fort-worth-dental-fort-worth"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>About Us | Fort Worth Dental | Family & Cosmetic Dentistry Since 1989</title>
        <meta name="description" content="Learn about Fort Worth Dental, your trusted family dentist in Fort Worth TX since 1989. Over 35 years of experience in dental implants, cosmetic dentistry, Invisalign, and comprehensive dental care." />
        <meta name="keywords" content="about Fort Worth Dental, Fort Worth dentist, family dentistry Fort Worth TX, cosmetic dentistry Fort Worth, best dentist near me, dental office fort worth" />
        <link rel="canonical" href="https://fortworthdental.com/about-us" />
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">About Fort Worth Dental</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Your trusted partner in dental health since 1989. Providing exceptional care to families throughout Fort Worth and the DFW metroplex.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Welcome to Fort Worth's Family Dentist</h2>
              
              <p className="text-gray-700 mb-6">
                Welcome to our family-friendly, state of the art facility. Our team and doctors are dedicated to improving 
                the health and appearance of smiles from Fort Worth, Aledo, and surrounding areas all the way to Stephenville. 
                We are an all-in-one practice that offers a large variety of services such as: basic preventive care, cosmetic 
                services like teeth whitening or restorative care with dental implants, veneers, Invisalign or laser gum disease 
                treatment (LANAP).
              </p>

              <p className="text-gray-700 mb-6">
                We can meet your needs under one roof. Dr. Mund, Dr. Razo, and Dr. Hiwiadi have the training and expertise 
                to handle most dental needs here in our office. We want your experience to be a positive one, whether it be 
                through sedation or simply through the fun, caring and friendly atmosphere our team creates.
              </p>

              <p className="text-gray-700 mb-6">
                Your smile is in good hands with the doctors and team at Fort Worth Dental. We look forward to taking care 
                of all your dental needs and establishing a relationship with you and your family. We can't wait to meet you.
              </p>
            </div>

            {/* Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
              <Link to="/our-team" className="bg-teal-50 hover:bg-teal-100 p-6 rounded-xl transition group">
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-teal-600">Meet Our Team</h3>
                <p className="text-gray-600">Learn about our experienced dentists and dedicated staff members.</p>
              </Link>
              <Link to="/our-offices" className="bg-teal-50 hover:bg-teal-100 p-6 rounded-xl transition group">
                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-teal-600">Our Offices</h3>
                <p className="text-gray-600">Visit our modern, state-of-the-art dental facility in Fort Worth.</p>
              </Link>
            </div>

            {/* Why Choose Us */}
            <div className="mt-16">
              <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">Why Choose Fort Worth Dental?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6">
                  <div className="text-4xl mb-4">🏆</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">35+ Years Experience</h3>
                  <p className="text-gray-600">Trusted by Fort Worth families for over three decades.</p>
                </div>
                <div className="text-center p-6">
                  <div className="text-4xl mb-4">🦷</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Comprehensive Care</h3>
                  <p className="text-gray-600">From preventive care to advanced implants, all under one roof.</p>
                </div>
                <div className="text-center p-6">
                  <div className="text-4xl mb-4">💝</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Patient-Focused</h3>
                  <p className="text-gray-600">Your comfort and satisfaction are our top priorities.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the Fort Worth Dental Difference?</h2>
          <p className="text-xl mb-8">Schedule your appointment today!</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:8179853337" className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition">
              Call (817) 985-3337
            </a>
            <Link to="/contact-us" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-teal-500 transition">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUs;
